import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { CreditCardIcon, SquareCheckIcon } from '../../../../../assets/icons';
import { setAddNewCardModal } from '../../../../../store/workspace/slice';
import Button from '../../../../../components/Button';
import DropDown from '../../../../../components/DropDown';
import Input from '../../../../../components/Input';
import Modal from '../../../../../components/Modal';
import { ListItem } from '../../../../../store/general/types';
import { useDispatch } from '../../../../../store/store';
import { setNotification } from '../../../../../store/general/slice';
import { xhrDefaultMessage } from '../../../../../configs/notification';
import { countries } from '../../../../../configs';
import styles from './style.module.scss';
import { useState } from 'react';
import {
    getPaymentMethodsThunk,
    attachPaymentMethodThunk,
} from '../../../../../store/workspace/thunks';

const cardElementStyles = {
    base: {
        display: 'flex',
        flexDirection: 'column',
        fontSize: '16px',
        color: '#424770',
        fontFamily: 'inherit',
        '::placeholder': {
            color: '#aab7c4',
        },
        border: '1px solid #ced4da',
        padding: '10px 12px',
        backgroundColor: '#fff',
        borderRadius: '4px',
    }
};

export default function AddNewCard() {
    const dispatch = useDispatch();
    const [CVC, setCVC] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [fullName, setFullName] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [cardHolderName, setCardHolderName] = useState('');
    const [billingAddress, setBillingAddress] = useState('');
    const [month, setMonth] = useState<ListItem | null>(null);
    const [year, setYear] = useState<ListItem | null>(null);
    const [country, setCountry] = useState<ListItem | null>(null);
    const [step, setStep] = useState<'CreditInfo' | 'BillingInfo' | 'Done'>('CreditInfo');
    const [loading, setLoading] = useState(false);

    const confirmBtnTitle = () => {
        switch (step) {
            case 'CreditInfo':
                return 'Continue';
            case 'BillingInfo':
                return 'Add card';
            case 'Done':
                return 'Confirm';
            default:
                return '';
        }
    };

    const isNextStepEnable = () => {
        switch (step) {
            case 'CreditInfo':
                return !fullName || !city || !country || !state;
            case 'BillingInfo':
                return !fullName || !billingAddress || !city || !country || !state;
            default:
                return false;
        }
    };

    const nextStep = async () => {
        switch (step) {
            case 'CreditInfo':
                handleSubmit();
                break;
            case 'Done':
                dispatch(setAddNewCardModal(false));
                break;
        }
    };

    const dismiss = () => {
        if (step === 'CreditInfo') {
            dispatch(setAddNewCardModal(false));
        } else {
            setStep('CreditInfo');
        }
    };

    const stripe = useStripe();
    const elements = useElements();

    const handleSubmit = async () => {
        const cardElement = elements?.getElement(CardElement);
        if (!stripe || !elements || !cardElement) return;

        setErrorMessage('');
        setLoading(true)
        const result = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
                name: fullName,
                email: '',
                address: {
                    line1: '',
                    city,
                    state,
                    postal_code: '',
                    country: country?.id,
                },
            }
        });

        if (result.error) {
            setLoading(false);
            setErrorMessage(result?.error?.message?.split('.')[0] || xhrDefaultMessage);
            dispatch(setNotification([result?.error?.message || xhrDefaultMessage, 'error']))
        } else {
            dispatch(attachPaymentMethodThunk({
                action: 'attach_payment_method',
                pmId: result.paymentMethod.id
            }))
                .unwrap()
                .then(() => {
                    setLoading(false);
                    setStep('Done');
                    dispatch(getPaymentMethodsThunk({
                        action: 'get_payment_methods',
                    }))
                })
                .catch(e => {
                    setLoading(false);
                    setErrorMessage(e?.message || xhrDefaultMessage);
                    dispatch(setNotification([xhrDefaultMessage, 'error']))
                })
        }
    };

    return (
        <Modal
            size='sm'
            childClassName={styles.wrapper}
            closeFunc={() => dispatch(setAddNewCardModal(false))}
            icon={step === 'Done' ? <SquareCheckIcon /> : <CreditCardIcon />}
            title={step === 'Done' ? 'Card Added' : 'Add new credit or debit card'}
        >
            {/* {step === 'CreditInfo' &&
                <div className={styles.credit_card}>
                    <span className={styles.credit_card_title}>Card info</span>
                    <Input
                        label='Card number'
                        inputMode='numeric'
                        placeholder=''
                        type='text'
                        value={cardNumber}
                        onChange={setCardNumber}
                    />
                    <Input
                        label='Cardholder name'
                        placeholder=''
                        type='text'
                        value={cardHolderName}
                        onChange={setCardHolderName}
                    />
                    <Input
                        label='CVC'
                        placeholder=''
                        type='text'
                        value={CVC}
                        onChange={setCVC}
                    />
                    <span className={styles.credit_card_expiration_title}>Expiration date</span>
                    <div className={styles.credit_card_expiration_date}>
                        <DropDown
                            idKey='id'
                            openListOnTop
                            titleKey='title'
                            placeholder='Month'
                            setItem={setMonth}
                            selectedItem={month}
                            dropDownList={creditCardExpirationMonth}
                        />
                        <DropDown
                            idKey='id'
                            openListOnTop
                            titleKey='title'
                            placeholder='Year'
                            setItem={setYear}
                            selectedItem={year}
                            dropDownList={creditCardExpirationYear}
                        />
                    </div>
                </div>}
            {step === 'BillingInfo' && <div className={styles.billing}>
                <span className={styles.billing_title}>Billing Info</span>
                <Input
                    label=' Full Name or Company Name'
                    placeholder=''
                    type='text'
                    value={fullName}
                    onChange={setFullName}
                />
                <Input
                    label='Billing Address'
                    placeholder=''
                    type='text'
                    value={billingAddress}
                    onChange={setBillingAddress}
                />
                <div className={styles.billing_city_and_zip}>
                    <Input
                        label='City'
                        placeholder=''
                        type='text'
                        value={city}
                        onChange={setCity}
                    />
                    <Input
                        label='Zip Code'
                        placeholder=''
                        type='text'
                        value={zipCode}
                        onChange={setZipCode}
                    />
                </div>
                <Input
                    label='Country'
                    placeholder=''
                    type='text'
                    value={country}
                    onChange={setCountry}
                />
            </div>}
            {step === 'Done' && <span className={styles.successfull}>
                Your card has been successfully added.
            </span>} */}
            {step === 'Done' && <span className={styles.successfull}>
                Your card has been successfully added.
            </span>}
            {step === 'CreditInfo' && <form>
                <span className={styles.cardElement_title}>Card Info</span>
                <div className={styles.cardElement}>
                    <CardElement options={{ style: cardElementStyles }} />
                </div>
                <div className={styles.billing}>
                    {/* <span className={styles.billing_title}>Billing Info</span> */}
                    <Input
                        label=' Full Name or Company Name'
                        placeholder=''
                        type='text'
                        value={fullName}
                        onChange={setFullName}
                    />
                    <Input
                        label='Billing Address'
                        placeholder=''
                        type='text'
                        value={billingAddress}
                        onChange={setBillingAddress}
                    />
                    <div className={styles.billing_city_and_zip}>
                        <Input
                            label='City'
                            placeholder=''
                            type='text'
                            value={city}
                            onChange={setCity}
                        />
                        <Input
                            label='State'
                            placeholder=''
                            type='text'
                            value={state}
                            onChange={setState}
                        />
                    </div>
                    <DropDown
                        idKey='id'
                        searchable
                        openListOnTop
                        titleKey='title'
                        label='Country'
                        setItem={setCountry}
                        selectedItem={country}
                        dropDownList={countries}
                    />
                    {/* <Input
                        label='Country'
                        placeholder=''
                        type='text'
                        value={country}
                        onChange={setCountry}
                    /> */}
                </div>
            </form>}
            {errorMessage && <span className={styles.error}>{errorMessage}</span>}
            <div className={styles.actions}>
                {step !== 'Done' && <Button
                    type='Outline-Primary'
                    text={step === 'CreditInfo' ? 'Cancel' : 'Back'}
                    className={styles.actions_btn}
                    onClick={dismiss}
                />}
                <Button
                    type='Primary'
                    onClick={nextStep}
                    text={confirmBtnTitle()}
                    disabled={!stripe || isNextStepEnable()}
                    loading={loading}
                    className={styles.actions_btn}
                />
            </div>
        </Modal>
    )
};
